import * as Sentry from "@sentry/react"

/**
 * Options for captureHandledError.
 */
export interface CaptureHandledErrorOptions {
  /** The X-Request-Id from the backend response, if available. */
  requestId?: string | null
}

/**
 * shouldCapture returns true when the error represents an unexpected failure
 * that Sentry should record — i.e. a 5xx server error or a network/parse/
 * unknown-shape failure that the UI has no branch for.
 *
 * Returns false for handled domain outcomes the UI already dispatches on:
 *   - 401 Unauthorized (session expired, Clerk handles it)
 *   - 403 Forbidden (MFA reverification, paywall — both have dedicated flows)
 *   - 409 with a known domain code (stale_snapshot, stale_target, etc.)
 *   - Any error whose ApiErrorBody.code the caller branches on
 *   - Clerk reverification hints (not server errors)
 *
 * The predicate lives once in @sill/shared so the three surfaces (consumer /
 * marketing / pro) cannot diverge. It is wired at each surface's api.ts failure
 * path; the render-boundary analog is AppErrorBoundary (same package).
 */
export function shouldCapture(err: unknown): boolean {
  // Non-Error / non-object unknowns — network-level failure, treat as capturable.
  if (err === null || err === undefined) {
    return false
  }

  // Clerk reverification hint shape: { clerk_error: { reason, type } }
  if (isClerkReverificationHint(err)) {
    return false
  }

  if (!isApiErrorLike(err)) {
    // Network error, parse error, TypeError from fetch — capturable.
    return true
  }

  const { status, structured } = err

  // 401 Unauthorized — session expired, handled by Clerk.
  if (status === 401) {
    return false
  }

  // 403 Forbidden — MFA reverification or paywall, both have dedicated flows.
  if (status === 403) {
    return false
  }

  // 409 Conflict — handled domain outcome (stale_snapshot, stale_target, etc.)
  if (status === 409) {
    return false
  }

  // Any error whose code the caller dispatches on is a handled domain outcome.
  // An ApiErrorBody with a non-empty `code` means the backend signaled a
  // machine-readable outcome the frontend has explicit handling for.
  if (structured?.code && structured.code !== "") {
    return false
  }

  // 5xx server errors are unexpected — capture.
  if (status >= 500) {
    return true
  }

  // 4xx other than handled above: e.g. 404 "not found" or 400 "bad request"
  // are typically handled by the caller. Do not capture — they are domain
  // outcomes with well-defined UI states.
  return false
}

/**
 * captureHandledError checks the error against the shouldCapture predicate and,
 * when it passes, sends one Sentry event tagged with the operation name and the
 * request_id (when available).
 *
 * Call from api.ts failure paths. Do NOT call from components — observability
 * capture belongs at the API boundary, not in render logic.
 */
export function captureHandledError(
  operation: string,
  err: unknown,
  { requestId }: CaptureHandledErrorOptions = {},
): void {
  if (!shouldCapture(err)) {
    return
  }
  Sentry.withScope((scope) => {
    scope.setTag("operation", operation)
    if (requestId) {
      scope.setTag("request_id", requestId)
    }
    Sentry.captureException(err instanceof Error ? err : new Error(String(err)))
  })
}

// --- type guards ---

interface ApiErrorLike {
  status: number
  structured?: { code?: string; [key: string]: unknown }
}

function isApiErrorLike(err: unknown): err is ApiErrorLike {
  return (
    typeof err === "object" &&
    err !== null &&
    "status" in err &&
    typeof (err as { status: unknown }).status === "number"
  )
}

function isClerkReverificationHint(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false
  const v = err as Record<string, unknown>
  return (
    "clerk_error" in v &&
    typeof v["clerk_error"] === "object" &&
    v["clerk_error"] !== null
  )
}
